import { type FC, useCallback, useMemo, useState } from 'react'
import classNames from 'classnames'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { Button } from '../../components/Button/Button'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import { selectRoomSession } from '../../store/meetingSummary/roomSession'
import { selectSelectedMeetingTemplate } from '../../store/meetingSummary/meetingTemplates'
import { useGetExternalUserByIdQuery } from '../../store/externalUser/externalUser'
import { type IntegrationServiceId } from '../../common/integrations'
import {
  type AccountOpeningCollection,
  type AccountOpeningCollectionStep,
  type AccountOpeningOperationStep,
  type AccountOpeningStep,
  type AccountOpeningStepId,
} from '../../models/AccountOpeningDescriptor'
import { resolveFlow, type StepStatus } from './domain/flow'
import { stepInvalidGroups, stepMissingGroups, type ItemGroup, type ResolvedItem } from './domain/collections'
import { useAccountTypeCatalog } from './hooks/useAccountTypeCatalog'
import { useCollectionPreparations } from './hooks/useCollectionPreparations'
import { AccountIntakeModal } from './intake/AccountIntakeModal'
import { contactOptionOf } from './domain/contactFallback'
import { ErrorText, StepActionBar } from './steps/flowPrimitives'
import { FlowSection } from './steps/FlowSection'
import { LoadingScreen } from './steps/LoadingScreen'
import { StepInfoRail } from './steps/StepInfoRail'
import { OpeningsEntryPanel } from './entry/OpeningsEntryPanel'
import { CollectionStep } from './steps/CollectionStep'
import { OperationStep } from './steps/OperationStep'
import { HandoffStep } from './steps/HandoffStep'
import { WizardFlowContext, type WizardFlow } from './hooks/WizardFlowContext'
import { useResolvedConnector } from './hooks/useResolvedConnector'
import { useFlowEntry } from './hooks/useFlowEntry'
import { useResumeOpening } from './hooks/useResumeOpening'
import { collectionCopy, flowCopy, WIZARD_ERRORS } from './descriptor/stepCopy'
import { useAccountOpeningWizard } from './hooks/useAccountOpeningWizard'
import { createEmptyAccount, type ContactOption, type ItemPatch, type WizardAccount, type WizardContext } from './domain/types'
import { accountOpeningSource, operationTrackingEvent, startedEvent } from './domain/tracking'
import { useTracking } from '../../apps/professional-app/fa/common/tracking'
import { type SubmissionOutcome } from './domain/connectorResult'
import styles from './AccountOpeningWizard.module.scss'

interface StepIssues {
  missing: ItemGroup[]
  invalid: ItemGroup[]
}

export const AccountOpeningWizard: FC = () => {
  const accountId = useAppSelector(selectAccountId)
  const roomSession = useAppSelector(selectRoomSession)
  const selectedMeetingTemplate = useAppSelector(selectSelectedMeetingTemplate)
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()

  const contactId = searchParams.get('contactId')
  const entryRoomId = searchParams.get('roomId')
  const entrySessionId = searchParams.get('sessionId')
  const roomId = entryRoomId ?? roomSession?.roomId ?? null
  const sessionId = entrySessionId ?? roomSession?.id ?? null
  const source = useMemo(() => accountOpeningSource(contactId, entryRoomId, entrySessionId), [contactId, entryRoomId, entrySessionId])
  const meetingTemplateId = searchParams.get('meetingTemplateId') ?? selectedMeetingTemplate?.id ?? null

  const { data: contactResponse } = useGetExternalUserByIdQuery({ accountId, userId: contactId ?? '' }, { skip: !contactId })
  const contact = contactResponse?.data ?? null
  const contactName = contact?.name ?? null

  const entryState = location.state as { intakeAccounts?: WizardAccount[]; intakeConnector?: IntegrationServiceId } | null
  const { connectors, connector, setConnector } = useResolvedConnector(entryState?.intakeConnector)

  const ctx: WizardContext = useMemo(
    () => ({ accountId, connector: connector ?? '', contactId, roomId, sessionId, meetingTemplateId }),
    [accountId, connector, contactId, roomId, sessionId, meetingTemplateId],
  )

  const catalog = useAccountTypeCatalog(accountId, connector ?? '', { refetchOnMountOrArgChange: true })
  const { descriptor, selectors, accountTypes, descriptorFailed } = catalog
  const copy = useMemo(() => flowCopy(descriptor), [descriptor])

  const contactOptions: ContactOption[] = useMemo(() => {
    const fromMeeting = (roomSession?.users ?? []).filter((user) => (user.roles ?? []).some((role) => role.name === 'Client')).map(contactOptionOf)
    if (contact && !fromMeeting.some((option) => option.id === contact.id)) {
      return [contactOptionOf(contact), ...fromMeeting]
    }
    return fromMeeting
  }, [roomSession, contact])

  const wizard = useAccountOpeningWizard({ ctx, accountTypes, selectors, descriptor, contactOptions })
  const {
    accounts,
    parties,
    steps,
    statuses,
    openingId,
    reachedStep,
    stepConfirmed,
    readOnly,
    collectionCtx,
    itemsCopy,
    confirmations,
    operationResults,
    operationRuns,
    submittedResult,
    savedMetadata,
    preparationSignatures,
    saveError,
    isSaving,
    canDiscard,
    editItem,
    applyIntake,
    confirmStep,
    discardStepChanges,
    confirmItem,
    hydrateCollection,
    prefillCollection,
    fillValues,
    recordOperationResult,
    startNew,
    loadOpening,
    reset: resetWizard,
  } = wizard

  const [intakeMode, setIntakeMode] = useState<'new' | 'edit' | null>(null)
  const { trackingClient } = useTracking()
  const [collapseOverrides, setCollapseOverrides] = useState<Partial<Record<AccountOpeningStepId, boolean>>>({})

  const preparations = useCollectionPreparations({
    steps,
    reachedStep,
    signatures: preparationSignatures,
    hydrate: hydrateCollection,
    fill: prefillCollection,
  })

  const stepIsFetching = (step: AccountOpeningStep): boolean => {
    if (step.kind !== 'collection') {
      return false
    }
    const preparation = preparations.of(step.collection)
    return preparation.isHydrating || (!!step.prefill && preparation.isFilling)
  }

  const preparationError = (collection: AccountOpeningCollection): string | null => {
    const preparation = preparations.of(collection)
    if (!preparation.failed) {
      return null
    }
    return preparation.failure === 'custodianDetails' ? WIZARD_ERRORS.custodianDetails : WIZARD_ERRORS.prepare
  }

  const stepIssues = (step: AccountOpeningCollectionStep): StepIssues =>
    stepIsFetching(step) ? { missing: [], invalid: [] } : { missing: stepMissingGroups(step, collectionCtx), invalid: stepInvalidGroups(step, collectionCtx) }

  const { resume, busyOpeningId, error: resumeError, reset: resetResume } = useResumeOpening({ accountId, catalog, loadOpening, setConnector })

  useFlowEntry({
    seededAccounts: entryState?.intakeAccounts ?? null,
    deepLinkOpeningId: searchParams.get('openingId'),
    deepLinkIsView: searchParams.get('mode') === 'view',
    startNew,
    resume,
  })

  const handleOpenNew = useCallback(() => {
    resetResume()
    setIntakeMode('new')
  }, [resetResume])

  const handleIntakeContinue = (intakeAccounts: WizardAccount[]) => {
    if (intakeMode === 'new') {
      trackingClient.track(startedEvent(source, intakeAccounts, accountTypes, selectors))
    }
    applyIntake(intakeAccounts)
    setIntakeMode(null)
  }

  const trackOperationRun = (step: AccountOpeningOperationStep, outcome: SubmissionOutcome | null) => {
    const event = operationTrackingEvent(step.operation, source, accounts, accountTypes, selectors, outcome)
    if (event) {
      trackingClient.track(event)
    }
  }

  const handleLeaveFlow = () => {
    if (contactId) {
      navigate(`/library/manage-contacts/contacts/${contactId}/tabs/accounts`)
      return
    }
    resetWizard()
    resetResume()
    setIntakeMode(null)
    setCollapseOverrides({})
    preparations.reset()
  }

  const handleItemChange = (step: AccountOpeningStep, item: ResolvedItem, patch: ItemPatch) => {
    editItem(item, patch, step.id)
  }

  const isCollapsed = (step: AccountOpeningStep, status: StepStatus): boolean => {
    if (status === 'inactive') {
      return true
    }
    const override = collapseOverrides[step.id]
    if (override !== undefined) {
      return override
    }
    return !readOnly && step.kind === 'collection' && status === 'done'
  }

  const toggleCollapsed = (step: AccountOpeningStep, status: StepStatus) => {
    setCollapseOverrides((previous) => ({ ...previous, [step.id]: !isCollapsed(step, status) }))
  }

  const stepInfoRail = (step: AccountOpeningStep, issues: StepIssues | null) => {
    if (readOnly || step.kind !== 'collection' || !issues) {
      return undefined
    }
    const railCopy = collectionCopy(step)
    return (
      <StepInfoRail
        heading={railCopy.railHeading}
        missingHeading={railCopy.railMissing}
        invalidHeading={railCopy.railInvalid}
        confirmLabel={railCopy.railConfirm}
        loading={stepIsFetching(step)}
        loadingMessage={railCopy.loading}
        error={preparationError(step.collection)}
        onRetry={() => preparations.retry(step.collection)}
        retryLabel={WIZARD_ERRORS.retry}
        groups={issues.missing}
        invalidGroups={issues.invalid}
        confirmed={step.confirm ? stepConfirmed(step) : undefined}
      />
    )
  }

  const entryPanel = (
    <div className='d-flex flex-column zocks-gap-1'>
      <OpeningsEntryPanel
        accountId={accountId}
        connectors={connectors}
        contactId={contactId}
        source={source}
        catalog={catalog}
        onOpenNew={handleOpenNew}
        onResume={(openingId) => resume(openingId, false)}
        onView={(openingId) => resume(openingId, true)}
        busyOpeningId={busyOpeningId}
      />
      {resumeError ? <ErrorText>{resumeError}</ErrorText> : null}
    </div>
  )

  const readOnlySteps = useMemo(() => (descriptor ? resolveFlow(descriptor, 'readOnly') : []), [descriptor])
  const visibleSteps = readOnly ? readOnlySteps : steps
  const visibleStatuses: Record<string, StepStatus> = readOnly ? Object.fromEntries(readOnlySteps.map((step) => [step.id, 'done' as StepStatus])) : statuses

  const flow = useMemo<WizardFlow>(
    () => ({
      ctx,
      accountTypes,
      selectors,
      contactOptions,
      defaultHolderName: contactName,
      defaultHolderContactId: contactId,
      readOnly,
      itemsCopy,
    }),
    [ctx, accountTypes, selectors, contactOptions, contactName, contactId, readOnly, itemsCopy],
  )

  const stepBody = (step: AccountOpeningStep, status: StepStatus, issues: StepIssues | null) => {
    switch (step.kind) {
      case 'collection':
        return (
          <CollectionStep
            step={step}
            collectionCtx={collectionCtx}
            isSaving={isSaving}
            isFetching={stepIsFetching(step)}
            gated={!readOnly && status === 'inactive'}
            missingGroups={issues?.missing ?? []}
            confirmedRefs={confirmations[step.id] ?? []}
            canDiscard={canDiscard(step.id)}
            onItemChange={(item, patch) => handleItemChange(step, item, patch)}
            onValuesFilled={(filled) => fillValues(step.collection, filled)}
            onConfirmItem={(item) => confirmItem(step, item.ref)}
            onConfirmAll={() => confirmStep(step)}
            onDiscardChanges={() => discardStepChanges(step.id)}
          />
        )
      case 'operation':
        return (
          <OperationStep
            ctx={ctx}
            step={step}
            accounts={accounts}
            parties={parties}
            descriptor={descriptor}
            result={operationResults[step.id] ?? null}
            lastRunAt={operationRuns[step.id]}
            committed={submittedResult !== null}
            ready={openingId !== null}
            onResult={(result) => recordOperationResult(step, result)}
            onRunFinished={(outcome) => trackOperationRun(step, outcome)}
          />
        )
      case 'handoff':
        return (
          <HandoffStep
            step={step}
            result={submittedResult}
            descriptor={descriptor}
            accountId={ctx.accountId}
            openingId={openingId}
            savedMetadata={savedMetadata}
          />
        )
    }
  }

  const flowPage = (
    <WizardFlowContext.Provider value={flow}>
      <div className={classNames('d-flex flex-column', styles.formSurface)}>
        {visibleSteps.map((step, index) => {
          const status = visibleStatuses[step.id] ?? 'inactive'
          const collapsed = isCollapsed(step, status)
          const issues = step.kind === 'collection' ? stepIssues(step) : null
          return (
            <FlowSection
              key={step.id}
              number={index + 1}
              icon={step.icon}
              title={readOnly ? (step.readOnlyTitle ?? step.title) : step.title}
              subtitle={step.subtitle}
              status={status}
              isLast={index === visibleSteps.length - 1}
              collapsed={collapsed}
              onToggleCollapsed={() => toggleCollapsed(step, status)}
              headerActions={
                step.kind === 'collection' && step.headerAction?.kind === 'editIntake' && !readOnly ? (
                  <Button variant='secondary' className='d-flex align-items-center zocks-gap-0.5' onClick={() => setIntakeMode('edit')}>
                    <i className='bi bi-pencil-square' />
                    {step.headerAction.label}
                  </Button>
                ) : undefined
              }
              hasInfoColumn={!readOnly}
              infoContent={stepInfoRail(step, issues)}
            >
              {stepBody(step, status, issues)}
            </FlowSection>
          )
        })}
        {saveError ? <ErrorText>{saveError}</ErrorText> : null}

        {!readOnly && submittedResult ? (
          <StepActionBar message={copy.doneMessage}>
            <Button variant='primary' onClick={handleLeaveFlow}>
              {copy.doneLabel}
            </Button>
          </StepActionBar>
        ) : null}
      </div>
    </WizardFlowContext.Provider>
  )

  const heading = readOnly ? copy.readOnlyHeading : copy.editHeading
  const showFlowPage = accounts.length > 0

  return (
    <div className='d-flex flex-column zocks-gap-3 zocks-py-2.0'>
      <nav className='paragraph-medium d-flex align-items-center zocks-gap-0.5'>
        {contactId ? (
          <>
            <Link to={`/library/manage-contacts/contacts/${contactId}`}>{contactName ?? 'Contact'}</Link>
            <span>/</span>
          </>
        ) : null}
        <span>{showFlowPage ? heading : 'Account Opening'}</span>
      </nav>

      {showFlowPage ? (
        <>
          <div className='d-flex align-items-center justify-content-between zocks-gap-1'>
            <div className='d-flex align-items-center zocks-gap-1'>
              <i className='bi bi-file-earmark-plus fs-2' />
              <h3 className='mb-0'>{heading}</h3>
            </div>
            <Button variant='secondary' className='d-flex align-items-center zocks-gap-0.5' onClick={handleLeaveFlow}>
              <i className='bi bi-arrow-left' />
              {copy.leaveLabel}
            </Button>
          </div>

          {descriptor ? (
            flowPage
          ) : descriptorFailed ? (
            <ErrorText>The account-opening flow could not be loaded. Reload the page to try again.</ErrorText>
          ) : (
            <LoadingScreen message='Loading the account-opening flow' />
          )}
        </>
      ) : (
        entryPanel
      )}

      {intakeMode ? (
        <AccountIntakeModal
          show
          connectors={connectors}
          connector={connector}
          onConnectorChange={setConnector}
          catalog={catalog}
          initialAccounts={accounts.length > 0 ? accounts : [createEmptyAccount()]}
          contactOptions={contactOptions}
          defaultHolderName={contactName}
          defaultHolderContactId={contactId}
          onCancel={() => setIntakeMode(null)}
          onContinue={handleIntakeContinue}
        />
      ) : null}
    </div>
  )
}
